import React, { useEffect, useState, useCallback } from "react";
import { Box, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header.jsx";
import ActionButton from "../../components/ActionButton";
import ThemedDataGrid from "../../components/ThemedDataGrid.jsx";
import TableActions from "../../components/TableActions.jsx";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { graphqlEndpoint } from "../../config.js";
import { READ_ALL_COMPANIES } from "../../graph/companies/queries.js";
import { READ_COMPANY_ADDRESSES_BY_COMPANY_ID } from "../../graph/companyAddresses/queries.js";
import { ARCHIVE_COMPANY_ADDRESS } from "../../graph/companyAddresses/mutations.js";

const client = new GraphQLClient(graphqlEndpoint);

const ManageCompanyAddresses = () => {
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbar();

  const [addresses, setAddresses] = useState([]);

  const fetchAddresses = useCallback(async () => {
    try {
      const { readAllCompanies } = await client.request(READ_ALL_COMPANIES);

      const results = await Promise.all(
        (readAllCompanies || []).map(async (company) => {
          const { readCompanyAddressesByCompanyID } = await client.request(
            READ_COMPANY_ADDRESSES_BY_COMPANY_ID,
            { companyID: company.id }
          );

          return (readCompanyAddressesByCompanyID || []).map((address) => ({
            ...address,
            id: address.id,
            companyName: company.tradingName || company.legalName,
          }));
        })
      );

      setAddresses(results.flat());
    } catch (err) {
      console.error("Failed to fetch company addresses:", err);
      setAddresses([]);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load company addresses",
      });
    }
  }, [setSnackbar]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const handleArchiveAddress = async (address) => {
    if (!address?.id) return;
    if (!window.confirm("Archive this address?")) return;

    try {
      await client.request(ARCHIVE_COMPANY_ADDRESS, { id: address.id });
      await fetchAddresses();

      setSnackbar({
        open: true,
        severity: "success",
        message: "Address archived successfully",
      });
    } catch (err) {
      console.error("Failed to archive company address:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to archive address",
      });
    }
  };

  const columns = [
    { field: "companyName", headerName: "Company", flex: 1.2 },
    { field: "label", headerName: "Label", flex: 0.8 },
    { field: "addressLine1", headerName: "Address Line 1", flex: 1.4 },
    { field: "town", headerName: "Town", flex: 0.8 },
    { field: "county", headerName: "County", flex: 0.8 },
    { field: "postcode", headerName: "Postcode", flex: 0.7 },
    { field: "country", headerName: "Country", flex: 0.9 },
    {
      field: "isPrimary",
      headerName: "Primary",
      flex: 0.7,
      renderCell: ({ value }) => (
        <Chip
          label={value ? "Yes" : "No"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <TableActions
          onEdit={() =>
            navigate(`/companyAddresses/edit/${row.id}`, {
              state: { redirectPath: "/manageCompanyAddresses" },
            })
          }
          onDelete={() => handleArchiveAddress(row)}
          hideCreate
        />
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="COMPANY ADDRESSES"
          subtitle="Manage all company addresses"
        />

        <ActionButton
          tone="success"
          onClick={() => navigate("/manageCompanies")}
        >
          Manage Companies
        </ActionButton>
      </Box>

      <ThemedDataGrid rows={addresses} columns={columns} />
    </Box>
  );
};

export default ManageCompanyAddresses;